/**
 * Analytics helper — centralisé pour GA4 + PostHog + Consent Mode v2.
 *
 * Architecture pensée pour permettre une migration future vers GTM sans
 * toucher aux composants métier — seule la fonction trackEvent() en
 * interne changerait.
 *
 * Principes stricts :
 * - Aucun tracker chargé avant consentement Analytics
 * - Aucun envoi depuis les domaines preview vers la propriété production
 * - Aucune donnée personnelle transmise (nom, email, téléphone, adresse, message)
 */

const GA4_ID = "G-DHKW8QKVPR";
const CONSENT_LS_KEY = "ma_consent";
const CONSENT_VERSION = 1;

/** Aucun tracker actif hors production myanimation.fr */
export const IS_ANALYTICS_HOST = typeof window !== "undefined" && window.location.hostname === "myanimation.fr";

/* ============================================================
 * Consent state
 * ============================================================ */

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export function hasAnalyticsConsent() {
  return getConsent()?.analytics === true;
}

export function hasMarketingConsent() {
  return getConsent()?.marketing === true;
}

export function saveConsent({ analytics, marketing }) {
  const state = {
    v: CONSENT_VERSION,
    analytics: !!analytics,
    marketing: !!marketing,
    ts: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_LS_KEY, JSON.stringify(state));
  } catch (_) {}
  applyConsentToTrackers(state);
  return state;
}

/* ============================================================
 * Consent Mode v2 — updates gtag runtime state
 * ============================================================ */

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () { window.dataLayer.push(arguments); };
  }
}

export function updateGtagConsent(analyticsGranted) {
  ensureDataLayer();
  window.gtag("consent", "update", {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    // ad_* restent en denied — aucun tracker publicitaire actif
  });
}

/* ============================================================
 * GA4 loader — chargé une seule fois, uniquement après consentement
 * ============================================================ */

let ga4Loaded = false;

function loadGA4Once() {
  if (ga4Loaded) return;
  if (!IS_ANALYTICS_HOST) return; // pas d'envoi depuis preview
  ensureDataLayer();
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, {
    send_page_view: false, // Option B — page_view manuel via React Router
    anonymize_ip: true,
    cookie_flags: "SameSite=Lax;Secure",
  });
  ga4Loaded = true;
}

/* ============================================================
 * PostHog loader — chargé une seule fois, uniquement après consentement
 * ============================================================ */

let posthogLoaded = false;

function loadPostHogOnce() {
  if (posthogLoaded) return;
  if (typeof window === "undefined") return;
  if (typeof window.__loadPostHog !== "function") return; // exposé par index.html
  window.__loadPostHog();
  posthogLoaded = true;
}

function unloadPostHog() {
  if (typeof window === "undefined") return;
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      window.posthog.opt_out_capturing();
      window.posthog.reset && window.posthog.reset();
    }
  } catch (_) {}
  // Nettoyage en 2 passes : PostHog peut recréer des clés pendant reset()
  clearPostHogStorage();
  setTimeout(clearPostHogStorage, 50);
  setTimeout(clearPostHogStorage, 300);
}

function clearPostHogStorage() {
  if (typeof window === "undefined") return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("ph_")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("ph_")) sessionStorage.removeItem(k);
    }
    // Suppression du cookie PostHog
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name.startsWith("ph_")) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.myanimation.fr`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  } catch (_) {}
}

function clearGA4Storage() {
  if (typeof window === "undefined") return;
  try {
    document.cookie.split(";").forEach((c) => {
      const name = c.split("=")[0].trim();
      if (name === "_ga" || name.startsWith("_ga_") || name === "_gid") {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.myanimation.fr`;
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      }
    });
  } catch (_) {}
}

/* ============================================================
 * applyConsentToTrackers — appelé après chaque saveConsent()
 * ============================================================ */

export function applyConsentToTrackers(state) {
  const consent = state || getConsent();
  const granted = consent?.analytics === true;
  updateGtagConsent(granted);
  if (granted) {
    loadGA4Once();
    loadPostHogOnce();
    // Flush des events bufferés AVANT le consentement
    // (ex: view_local_page au mount, click_quote avant acceptation)
    flushPendingEvents();
  } else {
    unloadPostHog();
    clearGA4Storage();
  }
}

/* ============================================================
 * initAnalytics — appelé une fois au démarrage de l'app
 * Applique le consentement stocké (silent), sans afficher le bandeau.
 * ============================================================ */

export function initAnalytics() {
  const consent = getConsent();
  if (consent) applyConsentToTrackers(consent);
}

/* ============================================================
 * Public API — utilisée par les composants
 * ============================================================ */

/**
 * Envoi d'un événement à GA4.
 * - Ne fait rien si pas de consentement Analytics (mais buffer en mémoire pour flush post-consent)
 * - Ne fait rien depuis les domaines preview (console.log uniquement)
 * - Refuse silencieusement les paramètres contenant des données personnelles
 */
export function trackEvent(name, params = {}) {
  const clean = stripPII(params);
  if (!IS_ANALYTICS_HOST) {
    // eslint-disable-next-line no-console
    console.log("[GA4 PREVIEW]", name, clean);
    return;
  }
  if (!hasAnalyticsConsent()) {
    // Buffer en mémoire — flushé par applyConsentToTrackers() dès que
    // l'utilisateur accepte les cookies. Garantit que view_local_page /
    // click_quote émis AVANT le clic sur « Tout accepter » ne sont pas perdus.
    // Taille bornée pour éviter toute fuite mémoire si le user ne consent jamais.
    if (pendingEvents.length < PENDING_EVENTS_LIMIT) {
      pendingEvents.push({ name, params: clean });
    }
    return;
  }
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, clean);
}

/* ============================================================
 * Pre-consent event buffer
 * ============================================================ */

const PENDING_EVENTS_LIMIT = 25;
let pendingEvents = [];

function flushPendingEvents() {
  if (pendingEvents.length === 0) return;
  if (!IS_ANALYTICS_HOST) return;
  if (!hasAnalyticsConsent()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const toSend = pendingEvents;
  pendingEvents = [];
  for (const { name, params } of toSend) {
    try {
      window.gtag("event", name, params);
    } catch (_) { /* ne jamais casser l'UX */ }
  }
}

/**
 * Envoi manuel d'un page_view — appelé depuis le hook usePageView().
 * Dédoublonnage : refuse de renvoyer le même (path+title) dans les 1500ms
 * pour se protéger contre un double appel accidentel.
 */
let _lastPvKey = "";
let _lastPvAt = 0;
export function trackPageView({ path, title }) {
  const key = `${path}|${title}`;
  const now = Date.now();
  if (key === _lastPvKey && now - _lastPvAt < 1500) return;
  _lastPvKey = key;
  _lastPvAt = now;
  if (!IS_ANALYTICS_HOST) {
    // eslint-disable-next-line no-console
    console.log("[GA4 PREVIEW] page_view", { path, title });
    return;
  }
  if (!hasAnalyticsConsent()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_location: `https://myanimation.fr${path || ""}`,
    page_path: path,
    page_title: title,
  });
}

/**
 * Filtre les données personnelles avant tout envoi à GA4.
 * Bloque les clés interdites même si un composant appelle par erreur.
 */
const PII_KEYS = new Set([
  "name", "nom", "prenom", "first_name", "last_name",
  "email", "mail",
  "phone", "tel", "telephone", "mobile",
  "address", "adresse", "postal_code", "code_postal", "city_name",
  "message", "content", "body",
  "contract_id", "quote_id", "invoice_id", "numero_contrat", "numero_devis",
  "user_id", "client_id",
]);

function stripPII(params) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (PII_KEYS.has(k.toLowerCase())) continue;
    out[k] = v;
  }
  return out;
}

/* ============================================================
 * Helpers de catégorisation (non-personnels)
 * ============================================================ */

/** Regroupe une ville en macro-zone pour respecter le k-anonymat. */
export function cityGroupFromValue(cityOrCP) {
  if (!cityOrCP) return "unknown";
  const s = String(cityOrCP).toLowerCase();
  if (/lille|tourcoing|roubaix|villeneuve|marcq|wasquehal|croix|mons-en-baroeul/.test(s)) return "metropole_lille";
  if (/dunkerque|calais|gravelines|malo|boulogne/.test(s)) return "nord_littoral";
  if (/arras|lens|béthune|bethune|hénin|cambrai|valenciennes|douai/.test(s)) return "pas_de_calais_hainaut";
  if (/bruxelles|mouscron|tournai|courtrai|kortrijk|comines|belgique/.test(s)) return "belgique";
  return "autre";
}

/** Regroupe un budget en tranche non-nominative. */
export function budgetRange(v) {
  const n = parseFloat(v);
  if (!isFinite(n) || n <= 0) return "unspecified";
  if (n < 1000) return "500-1000";
  if (n < 2000) return "1000-2000";
  if (n < 5000) return "2000-5000";
  return "5000+";
}

/* ============================================================
 * Lead context — propagation page locale → /contact
 * Stocke des catégories NON-personnelles seulement (slug ville,
 * type de service, path d'origine). Permet au formulaire de
 * contact de rattacher la demande à la page locale d'origine
 * sans jamais passer de données personnelles.
 * ============================================================ */

const LEAD_CTX_KEY = "ma_lead_ctx";
const LEAD_SENT_KEY = "ma_lead_sent"; // flag anti-doublon generate_lead

export function setLeadContext({ service_type, page_type, city, source_page }) {
  if (typeof window === "undefined") return;
  try {
    const ctx = {
      service_type: service_type || "",
      page_type: page_type || "",
      city: city || "",
      source_page: source_page || "",
      ts: Date.now(),
    };
    sessionStorage.setItem(LEAD_CTX_KEY, JSON.stringify(ctx));
  } catch (_) {}
}

export function getLeadContext() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(LEAD_CTX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire après 2 heures pour éviter la fuite de contexte entre sessions longues
    if (!parsed?.ts || Date.now() - parsed.ts > 2 * 60 * 60 * 1000) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/**
 * Envoi generate_lead avec dédoublonnage par identifiant unique —
 * passer la quote_id ou timestamp de soumission comme `dedupKey`.
 * Si la même clé a déjà été envoyée dans la session, on NE renvoie PAS.
 * Protège contre : double-clic sur submit, refresh/back après succès,
 * re-render qui déclencherait un deuxième tracking.
 */
export function trackGenerateLeadOnce(params = {}, { dedupKey } = {}) {
  if (typeof window === "undefined") return;
  if (!dedupKey) {
    // Pas de clé → on refuse de tracker (évite envoi non maîtrisé)
    return;
  }
  try {
    const prev = sessionStorage.getItem(LEAD_SENT_KEY);
    if (prev === String(dedupKey)) return; // déjà envoyé pour cette soumission
    sessionStorage.setItem(LEAD_SENT_KEY, String(dedupKey));
  } catch (_) {}
  trackEvent("generate_lead", params);
}
